import { useEffect, useState } from "react";
import {
  ArrowLeftOutlined,
  FundProjectionScreenOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import SegmentedComponent from "../../../../components/segmented/Segmented.jsx";
import Loader from "../../../../components/Loader.jsx";
import CreateLiveAccount from "../../dashboard/components/CreateLiveAccount.jsx";
import { GetAllMT5AccountApi } from "../../../../api/request/account.js";
import { GetPammTokenApi } from "../../../../api/request/pamm.js";
import { useResponsive } from "../../../../hooks/useResponsive.js";

const normalizePammPayload = (payload) => {
  if (!payload) return null;
  if (typeof payload === "string") {
    return { view: "app", url: payload };
  }
  return {
    view: payload.view || payload.View,
    url: payload.url || payload.Url,
    investorUrl: payload.investorUrl || payload.InvestorUrl,
    managerUrl: payload.managerUrl || payload.ManagerUrl,
    role: payload.role || payload.Role,
  };
};

const ROLE_CARDS = [
  {
    id: "investor",
    title: "Investor",
    description:
      "Allocate funds to money managers and track your PAMM investments from this live account.",
    icon: <UserOutlined className="text-2xl" />,
    action: "Continue as Investor",
  },
  {
    id: "manager",
    title: "Money Manager",
    description:
      "Offer a strategy, manage investor allocations, and run your PAMM account as a manager.",
    icon: <FundProjectionScreenOutlined className="text-2xl" />,
    action: "Continue as Money Manager",
  },
];

const RoleTag = ({ role }) => {
  if (role === "manager") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#E8F1FF] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
        <FundProjectionScreenOutlined />
        Money Manager
      </span>
    );
  }

  if (role === "investor") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#E8F8F0] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#1B7A4A]">
        <UserOutlined />
        Investor
      </span>
    );
  }

  if (role === "register") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F5F5F5] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#8B8B8B]">
        Not registered
      </span>
    );
  }

  return null;
};

const PAAM = () => {
  const { isMobile } = useResponsive();
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [pammSession, setPammSession] = useState(null);
  const [pammUrl, setPammUrl] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [hoveredRole, setHoveredRole] = useState(null);
  const [isTokenLoading, setIsTokenLoading] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(false);

  const getAccounts = async () => {
    setIsLoading(true);
    try {
      const { data } = await GetAllMT5AccountApi(true);
      if (data?.success) {
        const liveAccounts =
          data?.data?.filter((account) => account?.isLive) || [];
        setAccounts(liveAccounts);
        setSelectedAccount(liveAccounts[0]?.login ?? null);
      } else {
        setAccounts([]);
      }
    } catch (error) {
      setAccounts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getPammToken = async () => {
    if (!selectedAccount) return;
    setIsTokenLoading(true);
    setPammSession(null);
    setPammUrl(null);
    setSelectedRole(null);
    try {
      const params = { LoginId: selectedAccount };
      const { data } = await GetPammTokenApi(params);
      if (data?.success && data?.data) {
        const session = normalizePammPayload(data.data);
        setPammSession(session);
        if (session?.view === "register") {
          setPammUrl(null);
        } else if (session?.view === "choose" && session?.managerUrl) {
          setSelectedRole("manager");
          setPammUrl(session.managerUrl);
        } else {
          setPammUrl(session?.url || session?.managerUrl || session?.investorUrl || null);
          if (session?.role === "manager" || session?.role === "investor") {
            setSelectedRole(session.role);
          }
        }
      } else {
        setPammSession(null);
        setPammUrl(null);
      }
    } catch (error) {
      setPammSession(null);
      setPammUrl(null);
    } finally {
      setIsTokenLoading(false);
    }
  };

  const selectRole = (role) => {
    const url =
      role === "manager" ? pammSession?.managerUrl : pammSession?.investorUrl;
    if (!url) return;
    setSelectedRole(role);
    setPammUrl(url);
  };

  const changeRole = () => {
    setSelectedRole(null);
    setPammUrl(null);
    setIframeLoading(false);
  };

  useEffect(() => {
    getAccounts();
  }, []);

  useEffect(() => {
    setPammSession(null);
    setPammUrl(null);
    setSelectedRole(null);
    if (selectedAccount) getPammToken();
  }, [selectedAccount]);

  useEffect(() => {
    if (pammUrl) setIframeLoading(true);
  }, [pammUrl]);

  if (isLoading) return <Loader />;

  if (!accounts?.length) return <CreateLiveAccount />;

  const needsRoleChoice =
    (pammSession?.view === "choose" || pammSession?.view === "register") &&
    !pammUrl;
  const canSwitchRole =
    Boolean(pammUrl) &&
    (pammSession?.view === "choose" || pammSession?.view === "register");
  const isRegister = pammSession?.view === "register";
  const activeRole =
    selectedRole === "manager" || selectedRole === "investor"
      ? selectedRole
      : pammSession?.role === "manager" || pammSession?.role === "investor"
        ? pammSession.role
        : null;
  const headerRole = activeRole || (isRegister && !pammUrl ? "register" : null);

  return (
    <div className="h-full w-full space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <SegmentedComponent
          options={accounts.map((item) => item?.login)}
          value={selectedAccount}
          setValue={setSelectedAccount}
          block={isMobile}
        />
        {selectedAccount && (
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm text-[#8B8B8B]">
              Live account{" "}
              <span className="font-semibold text-[#2F2B3D]">{selectedAccount}</span>
            </p>
            {!isTokenLoading && <RoleTag role={headerRole} />}
          </div>
        )}
      </div>

      <div
        className={`relative w-full overflow-hidden rounded-xl border border-[#E8E8E8] bg-white ${
          isMobile
            ? "h-[calc(100dvh-280px)] min-h-[360px]"
            : "h-[calc(100vh-170px)]"
        }`}
      >
        {(isTokenLoading || iframeLoading) && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80">
            <Loader />
          </div>
        )}

        {needsRoleChoice ? (
          <div className="flex h-full w-full items-center justify-center overflow-y-auto p-4 sm:p-8">
            <div className="w-full max-w-3xl">
              <div className="mb-6 flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <TeamOutlined className="text-xl" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-[#2F2B3D]">
                    {isRegister ? "Get started with PAMM" : "Open PAMM"}
                  </h2>
                  <p className="mt-1 text-sm leading-6 text-[#6B6B6B]">
                    {isRegister
                      ? `Account ${selectedAccount} is not registered on PAMM yet. Choose a role to continue.`
                      : `Account ${selectedAccount} can open as Investor or Money Manager. Pick the workspace you want to use.`}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {ROLE_CARDS.map((card) => {
                  const isHovered = hoveredRole === card.id;
                  return (
                    <button
                      key={card.id}
                      type="button"
                      onClick={() => selectRole(card.id)}
                      onMouseEnter={() => setHoveredRole(card.id)}
                      onMouseLeave={() => setHoveredRole(null)}
                      onFocus={() => setHoveredRole(card.id)}
                      onBlur={() => setHoveredRole(null)}
                      className={`group flex h-full flex-col rounded-xl border bg-white p-5 text-left transition-all duration-200 ${
                        isHovered
                          ? "border-primary shadow-[0_8px_24px_rgba(47,91,234,0.12)]"
                          : "border-[#E8E8E8] hover:border-primary"
                      }`}
                    >
                      <div
                        className={`mb-4 flex h-12 w-12 items-center justify-center rounded-xl transition-colors ${
                          isHovered
                            ? "bg-primary text-white"
                            : "bg-primary/10 text-primary"
                        }`}
                      >
                        {card.icon}
                      </div>
                      <h3 className="text-lg font-semibold text-[#2F2B3D]">
                        {card.title}
                      </h3>
                      <p className="mt-2 flex-1 text-sm leading-6 text-[#6B6B6B]">
                        {card.description}
                      </p>
                      <span className="mt-5 inline-flex items-center text-sm font-semibold text-primary">
                        {card.action}
                        <span className="ml-1 transition-transform group-hover:translate-x-1">
                          →
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ) : pammUrl ? (
          <div className="flex h-full w-full flex-col">
            {(canSwitchRole || activeRole) && (
              <div
                className={`flex items-center gap-3 border-b border-[#E8E8E8] px-4 py-2 ${
                  canSwitchRole ? "justify-between" : "justify-end"
                }`}
              >
                {canSwitchRole && (
                  <button
                    type="button"
                    onClick={changeRole}
                    className="inline-flex items-center gap-2 text-sm font-medium text-[#6B6B6B] transition-colors hover:text-primary"
                  >
                    <ArrowLeftOutlined />
                    Change role
                  </button>
                )}
                <RoleTag role={activeRole} />
              </div>
            )}
            <iframe
              key={pammUrl}
              src={pammUrl}
              title="PAAM"
              frameBorder="0"
              width="100%"
              height="100%"
              className="min-h-0 flex-1 border-0"
              onLoad={() => setIframeLoading(false)}
            />
          </div>
        ) : (
          !isTokenLoading && (
            <div className="flex h-full w-full flex-col items-center justify-center gap-4 px-4 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#F5F5F5] text-[#8B8B8B]">
                <TeamOutlined className="text-xl" />
              </div>
              <p className="max-w-md text-base font-medium text-[#2F2B3D]">
                Unable to load PAMM for this account. Please try again later.
              </p>
              <button
                type="button"
                className="rounded-lg bg-primary px-4 py-2 font-medium text-white themeHover duration-500"
                onClick={getPammToken}
              >
                Try again
              </button>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default PAAM;
